import { Alert, Stack, Typography } from '@mui/material';
import { Outlet, useParams } from 'react-router';

import { Navigation } from './components/Navigation/Navigation';
import { ProjectLayoutSkeleton } from './components/ProjectLayoutSkeleton';
import styles from './ProjectLayout.module.scss';
import {
  customerHoverCardFields,
  employeeHoverCardFields,
} from '../portfolio/helpers';
import { useGetProjectById } from '@/api/typesense/projects/useGetProjectById';
import { EntityHoverCard } from '@/components/entity/EntityHoverCard';
import { Panels } from '@/components/layouts/Panels';
import { IconedLabel } from '@/components/ui/IconedLabel';
import { areaIcons } from '@/constants/areaIcons';

export const ProjectLayout = () => {
  const { projectId } = useParams<{ projectId: string }>();

  // Fetch the selected project
  const { data: project, isLoading, isError } = useGetProjectById(projectId);

  return (
    <Panels autoSaveId="projects-management-panels" defaultLayoutRatio="2:1">
      <Panels.Main
        panelContentProps={{
          hideOverflow: true,
          hasPaddingBottom: false,
        }}
      >
        {isLoading ? (
          <ProjectLayoutSkeleton />
        ) : isError || !project ? (
          <Stack alignItems="center" justifyContent="center" minHeight="400px">
            <Alert severity="error">Failed to load project</Alert>
          </Stack>
        ) : (
          <Stack gap={3} className={styles.Wrapper}>
            <Stack
              direction="row"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
            >
              <Stack>
                <Typography
                  variant="h3SemiBold"
                  color="primary"
                  className={styles.ProjectTitle}
                >
                  <span>{project.Name}</span>
                  <Typography
                    component="span"
                    variant="label"
                    color="secondary"
                  >
                    ({project.Number})
                  </Typography>
                </Typography>

                <Stack
                  direction="row"
                  alignItems="center"
                  // Use sx prop due to this issue:
                  // https://github.com/mui/material-ui/issues/47844
                  sx={{
                    rowGap: 1,
                    columnGap: 3,
                  }}
                  flexWrap="wrap"
                >
                  <EntityHoverCard
                    entityId={project.CustomerId}
                    config={customerHoverCardFields}
                  >
                    <IconedLabel
                      iconName={areaIcons.customers}
                      iconColor="secondary"
                      data-automation-id="project-customer-hover-card"
                    >
                      {project.CustomerName}
                    </IconedLabel>
                  </EntityHoverCard>
                  <EntityHoverCard
                    entityId={project.ProjectManagerId}
                    config={employeeHoverCardFields}
                  >
                    <IconedLabel
                      iconName={areaIcons.employees}
                      iconColor="secondary"
                      data-automation-id="project-manager-hover-card"
                    >
                      {project.ProjectManagerName}
                    </IconedLabel>
                  </EntityHoverCard>
                </Stack>
              </Stack>

              <Stack direction="row" alignItems="center">
                <Navigation projectId={projectId!} />
              </Stack>
            </Stack>

            <Outlet />
          </Stack>
        )}
      </Panels.Main>
      <Panels.Drawer>Details</Panels.Drawer>
    </Panels>
  );
};
